/*
Custom hook for bulk message operations (selection and bulk discard)
Author: Implementation for bulk discard feature
Date: 2025-01-15
*/
import { useState, useCallback } from 'react'

import { api } from '@/utils/api'
import { useAuth } from '@/hooks/useAuth'

interface BulkDiscardResponse {
  message: string
  discarded: number
  requested: number
}

interface BulkOperationsError {
  message: string
}

export const useBulkMessageOperations = () => {
  const [selectedMessageIds, setSelectedMessageIds] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<BulkOperationsError | null>(null)
  const { userData } = useAuth()

  // Toggle selection of individual message
  const toggleMessageSelection = useCallback((messageId: string) => {
    setSelectedMessageIds((prev) =>
      prev.includes(messageId) ? prev.filter((id) => id !== messageId) : [...prev, messageId]
    )
  }, [])

  // Select all messages from provided list
  const selectAllMessages = useCallback((messageIds: string[]) => {
    setSelectedMessageIds(messageIds)
  }, [])

  // Clear all selections
  const clearSelection = useCallback(() => {
    setSelectedMessageIds([])
  }, [])

  // Check if message is selected
  const isMessageSelected = useCallback(
    (messageId: string) => {
      return selectedMessageIds.includes(messageId)
    },
    [selectedMessageIds]
  )

  // Bulk discard selected messages
  const bulkDiscardMessages = useCallback(async (): Promise<BulkDiscardResponse | null> => {
    if (selectedMessageIds.length === 0) {
      setError({ message: 'No messages selected for discarding' })

      return null
    }

    if (!userData?.profile?._id) {
      setError({ message: 'User profile not available' })

      return null
    }

    setIsLoading(true)
    setError(null)

    try {
      const response = await api.post<BulkDiscardResponse>('message_bulk_discard', {
        messageIds: selectedMessageIds,
        profileId: userData.profile._id,
      })

      // Clear selection after successful discard
      setSelectedMessageIds([])

      return response.data
    } catch (err: any) {
      const errorMessage = err?.response?.data?.error || 'Error discarding messages'
      setError({ message: errorMessage })

      return null
    } finally {
      setIsLoading(false)
    }
  }, [selectedMessageIds, userData?.profile?._id])

  return {
    selectedMessageIds,
    selectedCount: selectedMessageIds.length,
    isLoading,
    error,
    toggleMessageSelection,
    selectAllMessages,
    clearSelection,
    isMessageSelected,
    bulkDiscardMessages,
  }
}
